import type { ProjectIntent, ProjectPhase, SiteRagReport } from '../types/siteRag';
import { effectivePhaseCapacity, effectivePhases } from './projectIntent';
import { isIntentFieldExplicitlyUnknown } from './intent/explicitUnknown';

type DevelopmentPathwayEntry = NonNullable<SiteRagReport['developmentPathway']>[number];

/** Resolve owner buildout identity, never a row index or a generic work stage.
 * A conflicting title (row Phase 3, title Phase 1 construction) is not Phase 3.
 * A phase named in activity prose alone cannot assign that activity a milestone. */
export function resolveDevelopmentPathwayPhase(
  entry: DevelopmentPathwayEntry,
  phases: readonly ProjectPhase[],
): ProjectPhase | undefined {
  const label = entry.phase.trim().toLowerCase();
  if (/^work stage\b/.test(label)) return undefined;
  const ordinal = /^phase\s*(\d+)(?:\s*\([^)]*\))?$/.exec(label)?.[1];
  const phase = phases.find(p => label === p.label?.trim().toLowerCase()
    || (ordinal != null && p.sequence === Number(ordinal)));
  if (!phase) return undefined;
  const titlePhase = /\bphase\s*(\d+)\b/i.exec(entry.title)?.[1];
  if (titlePhase != null) return Number(titlePhase) === phase.sequence ? phase : undefined;
  // Generic task sequences were previously numbered Phase 1/2/3 by the
  // narrative schema. They do not establish a cumulative owner load.
  if (/\b(?:due diligence|site characterization|environmental review|permitting|engineering|procurement|construction|commissioning)\b/i.test(entry.title)) return undefined;
  return phase;
}

/** Pin only identified owner milestones to cumulative declared load. Keep
 * investigation/construction activities as work stages with no inherited load,
 * staffing or finance result. Insert missing owner milestones explicitly when
 * a model supplied only work stages; never fabricate generation adequacy. */
export function reconcileDevelopmentPathwayCapacities(
  developmentPathway: DevelopmentPathwayEntry[] | undefined,
  intent: ProjectIntent | null | undefined,
): DevelopmentPathwayEntry[] | undefined {
  if (!developmentPathway?.length || !intent) return developmentPathway;
  let view: ReturnType<typeof effectivePhases>;
  try { view = effectivePhases(intent); } catch { return developmentPathway; }
  const phases = view.phases.filter(p => (typeof p.capacityMW === 'number'
    && Number.isFinite(p.capacityMW) && p.capacityMW > 0)
    || (p.capacityBucket != null && p.capacityBucket !== 'undecided'));
  if (!phases.length) return developmentPathway;
  const basis = isIntentFieldExplicitlyUnknown(intent, 'powerBasis') || !intent.powerBasis
    ? 'cumulative declared MW; load boundary unconfirmed'
    : intent.powerBasis === 'it-load' ? 'cumulative IT load' : 'cumulative facility load';
  const capacity = (phase: ProjectPhase) => {
    if (typeof phase.capacityMW === 'number' && Number.isFinite(phase.capacityMW) && phase.capacityMW > 0) {
      return `${Number(phase.capacityMW.toFixed(1))} MW (${basis})`;
    }
    const cap = effectivePhaseCapacity(phase);
    return `${cap.displayLabel} (${basis}; exact point undeclared)`;
  };
  const represented = new Set<string>();
  const rows = developmentPathway.map((entry, index): DevelopmentPathwayEntry => {
    const phase = resolveDevelopmentPathwayPhase(entry, phases);
    if (phase) {
      represented.add(phase.id);
      // A bare MW heading otherwise looks cumulative even when the model used
      // a phase addition. Keep explicitly scoped equipment/addition headings;
      // pin an unqualified leading load number to this same owner milestone.
      const scopedTitle = /\b(?:incremental|addition|additional|generation|nameplate|BESS|battery|turbine)\b/i.test(entry.title);
      const title = scopedTitle ? entry.title : entry.title.replace(/^\s*[\d,]+(?:\.\d+)?\s*(?:MW|GW)\b(?!h)(?:\s*\(cumulative[^)]*\))?/i, capacity(phase));
      return { ...entry, title, capacityMW: capacity(phase) };
    }
    // A generation nameplate or an activity estimate cannot be relabelled as
    // the campus load merely because it exceeds a numeric ceiling.
    const { capacityMW: _capacity, cod: _cod, lcoe: _lcoe,
      firmCapacityVerdict: _firm, timeline: _timeline, ...activity } = entry as DevelopmentPathwayEntry & { timeline?: unknown };
    return { ...activity, phase: /^work stage\b/i.test(entry.phase) ? entry.phase : `Work stage ${index + 1}` };
  });
  const missing = phases.filter(phase => !represented.has(phase.id)).map((phase): DevelopmentPathwayEntry => ({
    phase: `Phase ${phase.sequence}`,
    title: `${phase.label || `Phase ${phase.sequence}`} capacity milestone`,
    description: `Owner-declared planning milestone: ${capacity(phase)}. This is a load target; generation output, utility service and delivery commitments require separate verification.`,
    capacityMW: capacity(phase),
    ...(phase.targetInServiceDate ? { cod: `${phase.targetInServiceDate} (owner target)` } : {}),
  }));
  return represented.size ? [...rows, ...missing] : [...missing, ...rows];
}
